class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active') || this.openingPromise) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Never reveal the initial Liquid drawer DOM. The shared cart coordinator
    // waits for any queued mutation, verifies Shopify, and refreshes every
    // cart surface from that one confirmed snapshot before opening.
    this.openingPromise = this.refresh()
      .then(() => this.openAfterRefresh())
      .catch((error) => console.error(error))
      .finally(() => {
        this.openingPromise = null;
      });
  }

  openAfterRefresh() {
    if (this.classList.contains('active')) return;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  refresh() {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = CartItems.refreshConfirmedCart({ source: 'drawer-open' })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents() {
    return this.refresh().then(() => this.openAfterRefresh());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];

    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    const cartRecommendations = document.querySelector('[data-op-cart-recommendations]');

    if (mainCartItems && mainCartFooter) {
      sections.push(
        {
          id: 'main-cart-items',
          section: mainCartItems.dataset.id,
          mount: 'main-cart-items',
          selector: '.js-contents',
        },
        {
          id: 'main-cart-footer',
          section: mainCartFooter.dataset.id,
          mount: 'main-cart-footer',
          selector: '.js-contents',
        }
      );
    }

    if (cartRecommendations) {
      sections.push({
        id: cartRecommendations.id,
        section: cartRecommendations.dataset.sectionId,
        selector: `#${cartRecommendations.id}`,
      });
    }

    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];

    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    if (mainCartItems && mainCartFooter) {
      sections.push(
        {
          id: 'main-cart-items',
          section: mainCartItems.dataset.id,
          selector: '.js-contents',
        },
        {
          id: 'main-cart-footer',
          section: mainCartFooter.dataset.id,
          selector: '.js-contents',
        }
      );
    }

    const cartRecommendations = document.querySelector('[data-op-cart-recommendations]');
    if (cartRecommendations) {
      sections.push({
        id: cartRecommendations.id,
        section: cartRecommendations.dataset.sectionId,
        selector: `#${cartRecommendations.id}`,
      });
    }

    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
